/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import {
    type BaseRequestOptionsArgs, type HttpMethod, type HttpResponse, type Rest, CookieService, EventService, ExtendService,
    HttpService, I18nService, LoadingService, LogService, SessionStorageService, ToastService, UtilService,
} from '@dm/core';
import { AuthService } from 'app/common/services/auth.service';
import { FeatureConfig } from 'app/config/feature';
import { environment } from 'environments/environment';
import { isObject } from 'lodash';
import { type Observable } from 'rxjs';

import { NO_PERMISSION_ERROR, runningStatusE, SYSTEM_VSTORE_RES_BATCH, SYSTEM_VSTORE_RES_COUNT, SYSTEM_VSTORE_RES_SINGLE } from '../constants';
import { lunToNamespaceMap, namespaceToLunMap } from '../constants/namespace-key';
import { type CommonCount, type CommonHttpResponse, type Lun, type System, type Vstore } from '../types/mo';
import { RemoteExecuteService } from './remote-execute.service';

// 鉴权失败错误码
const unAuthCode = -401;
// 会话被踢
const forceLogoutCode = 1077949069;
// 登录IP和当前使用的IP不一致
const ipNotMatchCode = 1073793620;
// 会话失效，操作无法授权
const noRightCode = 1073793621;

type IgnoreFn = (res: HttpResponse, options?: BaseRequestOptionsArgs) => boolean;

@Injectable({ providedIn: 'root' })
export class AppHttpService extends HttpService {
    // 是否正在升级
    isUpgrading = false;

    constructor(
        http: HttpClient,
        i18nService: I18nService,
        cookieService: CookieService,
        loadingService: LoadingService,
        logService: LogService,
        sessionStorageService: SessionStorageService,
        ext: ExtendService,
        private injector: Injector,
        private eventService: EventService,
        private utilService: UtilService,
        private toastService: ToastService,
        private remoteExe: RemoteExecuteService,
    ) {
        super(http, loadingService, i18nService, cookieService, logService, sessionStorageService, ext);
        this.eventService.subscribe('systemInfoUpdate', (systemInfo: System) => {
            this.isUpgrading = Number(systemInfo.RUNNINGSTATUS) === runningStatusE.Upgrading.value;
        });
    }

    /**
     * 为请求选项添加必要字段
     * @param method 请求方法
     * @param rest rest对象
     * @param options 外部传入的已有选项
     */
    getRequestOptionArgs(method: HttpMethod, rest: Rest, options: BaseRequestOptionsArgs, oriBody?: any): BaseRequestOptionsArgs {
        let body = oriBody;
        if (FeatureConfig.IsReplaceStrLunWithNamespace) {
            // 微存储 【lun ---> namespace】
            if (body) {
                body = this.lunToNamespaceModel(body);
            }
            if (rest && (rest as any).baseParams) {
                (rest as any).baseParams = this.lunToNamespaceModel((rest as any).baseParams);
            }
            if (rest && (rest as any).baseFilters) {
                (rest as any).baseFilters = this.lunToNamespaceFilterModel((rest as any).baseFilters);
            }

            if (rest && (rest as any).tempFilters) {
                (rest as any).tempFilters = this.lunToNamespaceFilterModel((rest as any).tempFilters);
            }
        }
        if (!FeatureConfig.IsSupportVStoreService) {
            // 微存储屏蔽多租户
            if (rest && (rest as any).baseParams && (rest as any).baseParams.vstoreId) {
                delete (rest as any).baseParams.vstoreId;
            }
            if (body && body.vstoreId) {
                delete body.vstoreId;
            }

            // 该标记会默认加上全局的租户ID，微存储屏蔽掉
            if (rest && rest.vstore) {
                rest.vstore = false;
            }
        }

        return super.getRequestOptionArgs(method, rest, options, body);
    }

    /**
     * 适配响应结果
     * @param res
     * @param rest
     */
    adaptResponse(oriRes: any, rest: Rest): HttpResponse {
        let responseNew: HttpResponse;

        if (FeatureConfig.IsReplaceStrLunWithNamespace) {
            responseNew = this.adaptNameSpaceRes(oriRes, rest);

            return responseNew;
        } else {
            const res = this.handleVstoreRes(oriRes, rest);

            if (rest.type === 'IBASE') {
                responseNew = {
                    ...res,
                    result: {
                        code: res.error.code,
                        description: res.error.description,
                        suggestion: res.error.suggestion,
                    },
                    data: res.data,
                };
                delete responseNew.error;
            } else if (rest.type === 'V2') {
                responseNew = {
                    ...res,
                    result: {
                        code: res.result.code,
                        description: res.result.description,
                        suggestion: res.result.suggestion,
                    },
                    data: res.data,
                };
            } else {
            }

            return responseNew;
        }
    }

    adaptNameSpaceRes(oriRes: HttpResponse, rest: Rest): HttpResponse {
        let responseNew: HttpResponse;
        if (oriRes?.data && (Array.isArray(oriRes.data) || isObject(oriRes.data))) {
            oriRes.data = this.namespaceToLunView(oriRes.data);
        }

        if (rest.type === 'IBASE') {
            responseNew = {
                ...oriRes,
                result: {
                    code: oriRes.error.code,
                    description: oriRes.error.description,
                    suggestion: oriRes.error.suggestion,
                },
                data: oriRes.data,
            };
            delete responseNew.error;
        } else if (rest.type === 'V2') {
            responseNew = {
                ...oriRes,
                result: {
                    code: oriRes.result.code,
                    description: oriRes.result.description,
                    suggestion: oriRes.result.suggestion,
                },
                data: oriRes.data,
            };
        } else {
        }

        return responseNew;
    }

    /**
     * 处理由于租户接口从base权限修改为vstore权限导致的界面配置
     */
    handleVstoreRes(res: any, rest: Rest): CommonHttpResponse<Vstore | Vstore[] | CommonCount> {
        if (rest.type !== 'IBASE') {
            return res;
        }

        const isNoPermission = NO_PERMISSION_ERROR.includes(Number(res.error.code));
        if (!isNoPermission) {
            return res;
        }

        if (rest.namespace === 'IBASE.mo.vstore.getBatch') {
            return SYSTEM_VSTORE_RES_BATCH;
        } else if (rest.namespace === 'IBASE.mo.vstore.count') {
            return SYSTEM_VSTORE_RES_COUNT;
        } else if (rest.namespace === 'IBASE.mo.vstore.get') {
            // 租户用户场景，支持从登陆信息中读取租户名称和ID
            return this.getNoPermissionSingleVstoreRes();
        }

        return res;
    }

    /**
     * 判断请求是否为会话失效
     * @param res 请求响应
     */
    isSessionInvalid(res: HttpResponse): boolean {
        const code = res.result.code;
        const invalidCodes = [unAuthCode, forceLogoutCode, ipNotMatchCode];

        if (invalidCodes.includes(code)) {
            return true;
        }

        return false;
    }

    /**
     * 处理会话失效场景
     * @param res 请求响应
     */
    handleInvalid(res: HttpResponse): void {
        const code = res.result.code;

        // 非-401下需要先下发退出登录的接口
        if ([forceLogoutCode, ipNotMatchCode].includes(code)) {
            const rest = this.makeUrl('user.logout');
            this.delete(rest).subscribe();
        }

        // IP不一致时，需要显示提示信息
        if (code === ipNotMatchCode) {
            this.handleException(res.rest, res);
        }

        this.eventService.emit('session_invalid');
    }

    /**
     * 处理http成功信息
     * @param rest rest
     */
    handleSuccess(rest: Rest, options?: BaseRequestOptionsArgs): void {
        const nameParams = options && options.restNameParams || rest.nameParams;
        let msg: string;

        if (options && options.name) {
            msg = this.i18nService.get('common.rest_success_label', [options.name]);
        } else if (rest.name) {
            msg = this.i18nService.get('common.rest_success_label', [
                this.i18nService.get('rest.' + rest.name, nameParams),
            ]);
        } else {
            msg = this.i18nService.get('common.downloadsuccess_label');
        }

        msg = this.utilService.encodeHTML(msg);
        this.toastService.success(`<div class="word-break text-wrap">${msg}</div>`, { safeHtml: true });
    }

    /**
     * 处理http异常信息
     * @param rest rest
     * @param res 返回结果
     * @param options 选项
     */
    handleException(rest: Rest, res: HttpResponse, options?: BaseRequestOptionsArgs): void {
        // 屏蔽一些特殊错误提示
        if (this.getIgnoreFnResult(res, options)) {
            return;
        }

        // 升级中get请求不报错
        if (this.isUpgrading && options && options.method === 'GET') {
            return;
        }

        const nameParams = options && options.restNameParams || rest.nameParams;

        let msg: string;
        if (options && options.name) {
            msg = this.i18nService.get('common.rest_fail_label', [options.name]);
        } else if (rest.name) {
            msg = this.i18nService.get('common.rest_fail_label', [
                this.i18nService.get(`rest.${rest.name}`, nameParams),
            ]);
        } else {
            msg = this.i18nService.get('common.requestFailed_label');
        }

        msg = `<div class="word-break text-wrap">${this.utilService.encodeHTML(msg)}</div>`;
        msg += `<div class="word-break text-wrap">${this.i18nService.get('common.description_column', true) +
            this.utilService.encodeHTML(res.result.description)
            }</div>`;
        if (res.result.suggestion) {
            msg += `<div class="word-break text-wrap">${this.i18nService.get('common.suggest_label', true) +
                this.utilService.encodeHTML(res.result.suggestion)
                }</div>`;
        }

        this.toastService.error(msg, { safeHtml: true });
    }

    /**
     * 在远端设备上执行请求
     * @param deviceId 远端设备ID
     * @param rest 远端请求Rest对象
     * @param method 请求方法
     * @param body 请求发送的数据
     */
    remoteExecute(deviceId: number, rest: Rest, method: HttpMethod, body: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.remoteExe.remoteExecute(deviceId, rest, method, body, options);
    }

    // 实现抽象方法
    proxyExecute(): void { }

    /**
     * 是否记录错误日志
     */
    isSkipLogError(res: HttpResponse): boolean {
        // release模式下不需要记录日志
        if (environment.env === 'release' || environment.env === 'release_crack') {
            return true;
        }

        // 查询升级进度错误不记录日志，1073884702这个错误码表示不在进行升级
        if (res.rest.namespace === 'IBASE.mo.upgrade.checkProgress' && res.result.code === 1073884702) {
            return true;
        }

        // 其余的不需要记录错误码的请求
        const skipErrorCodes: number[] = [
            1073884719, // 查询升级状态时未在升级
            83887409, // 日志打包未完成
            503, // 性能数据存储空间尚未创建，监控服务当前不可用。
        ];

        return skipErrorCodes.includes(res.result.code);
    }

    getNoPermissionSingleVstoreRes(): CommonHttpResponse<Vstore> {
        const authService = this.injector.get(AuthService, null);

        const loginVstoreName = authService.getVstoreName();

        if (loginVstoreName) {
            return {
                data: {
                    ID: authService.getVstoreId(),
                    NAME: loginVstoreName,
                },
                error: { code: 0, description: '0' },
            };
        } else {
            return SYSTEM_VSTORE_RES_SINGLE;
        }
    }

    /**
     * 微存储【namespace ---> lun】映射给DM使用
     */
    namespaceToLunView(oriData: any): Lun[] {
        let data = oriData;
        let newBody = [];
        const isDataArray = Array.isArray(data);
        if (!isDataArray) {
            data = [data];
        }
        const newData = data.map((item) => {
            const temp = {};
            for (const key in item) {
                if (Object.prototype.hasOwnProperty.call(item, key)) {
                    if (!namespaceToLunMap[key]) {
                        temp[key] = item[key];
                    } else {
                        temp[namespaceToLunMap[key]] = item[key];
                    }
                }
            }
            return temp;
        });
        if (!isDataArray) {
            newBody = newData[0];
        } else {
            newBody = newData;
        }

        return newBody;
    }

    /**
     * 微存储【lun ---> namespace】映射后发给后端
     */
    lunToNamespaceModel(oriData: any): unknown[] {
        let data = oriData;
        let newBody = [];
        const isDataArray = Array.isArray(data);
        if (!isDataArray) {
            data = [data];
        }

        const newData = data.map((item) => {
            const temp = {};
            for (const key in item) {
                if (Object.prototype.hasOwnProperty.call(item, key)) {
                    if (!lunToNamespaceMap[key]) {
                        temp[key] = item[key];
                    } else {
                        temp[lunToNamespaceMap[key]] = item[key];
                    }
                }
            }
            return temp;
        });
        if (!isDataArray) {
            newBody = newData[0];
        } else {
            newBody = newData;
        }

        return newBody;
    }

    /**
     * 微存储【lun ---> namespace】映射后发给后端【查询请求filter参数】
     */
    lunToNamespaceFilterModel(oriData: any): Lun[] {
        let data = oriData;
        let newBody = [];
        const isDataArray = Array.isArray(data);
        if (!isDataArray) {
            data = [data];
        }
        const newData = data.map((item) => {
            if (lunToNamespaceMap[item.field]) {
                item.field = lunToNamespaceMap[item.field];
            }
            return item;
        });
        if (!isDataArray) {
            newBody = newData[0];
        } else {
            newBody = newData;
        }

        return newBody;
    }

    /**
     * 屏蔽无权限错误提示
     * @param res 返回结果
     * @param options 选项
     */
    private checkNoPermissionFn: IgnoreFn = (res: HttpResponse, options?: BaseRequestOptionsArgs) =>
        (!options || (options.skipNoPermissionException !== false && options.method === 'GET')) &&
        NO_PERMISSION_ERROR.includes(res.result.code);

    /**
     * 屏蔽会话失效，操作无法授权错误提示
     * @param res 返回结果
     */
    private checkSessionRightFn: IgnoreFn = (res: HttpResponse) => res.result.code === noRightCode;

    private getIgnoreFnResult(res: HttpResponse, options?: BaseRequestOptionsArgs): boolean {
        const ignoreFnList: IgnoreFn[] = [
            this.checkNoPermissionFn,
            this.checkSessionRightFn,
        ];
        return ignoreFnList.some((item) => item(res, options));
    }
}
